import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { http } from "@/lib/admin";
import { Login, Center } from "@/pages/Admin";
import { QuoteDocument } from "@/components/admin/QuoteDocument";
import { ArrowLeft, Download, Printer } from "lucide-react";

export default function QuotePreview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [quote, setQuote] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const docRef = useRef(null);

  useEffect(() => {
    http.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false)).finally(() => setChecking(false));
  }, []);

  useEffect(() => {
    if (!user) return;
    http.get(`/quotes/${id}`).then((r) => setQuote(r.data)).catch(() => toast.error("Az árajánlat nem található"));
  }, [user, id]);

  const download = async () => {
    setDownloading(true);
    try {
      const html2pdf = (await import("html2pdf.js")).default;
      const safeName = quote.customer.name.replace(/[^\p{L}\p{N}]+/gu, "_");
      await html2pdf()
        .set({
          margin: 0,
          filename: `Arajanlat_${quote.number}_${safeName}.pdf`,
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, backgroundColor: "#faf8f5", windowWidth: 794 },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
          pagebreak: { mode: ["css", "legacy"] },
        })
        .from(docRef.current)
        .save();
    } catch (e) {
      toast.error("A PDF létrehozása nem sikerült");
    } finally {
      setDownloading(false);
    }
  };

  if (checking) return <Center>Betöltés...</Center>;
  if (!user) return <Login onLogin={setUser} />;

  return (
    <div className="min-h-screen bg-[#2a2d32]">
      <header className="print:hidden bg-white border-b border-[var(--line)] px-6 lg:px-10 h-16 flex items-center justify-between sticky top-0 z-40">
        <button data-testid="quote-preview-back" onClick={() => navigate("/admin")}
          className="inline-flex items-center gap-1.5 text-sm text-[var(--ink-soft)] hover:text-[var(--terra)]">
          <ArrowLeft className="w-4 h-4" /> Vissza az adminba
        </button>
        {quote && (
          <div className="flex items-center gap-2">
            <span className="font-mono-d text-xs text-[var(--ink-muted)] hidden sm:inline mr-2">{quote.number}</span>
            <button data-testid="quote-print-btn" onClick={() => window.print()}
              className="inline-flex items-center gap-1.5 rounded-full border border-[var(--line)] px-4 py-2 text-xs font-semibold hover:border-[var(--ink)]">
              <Printer className="w-3.5 h-3.5" /> Nyomtatás
            </button>
            <button data-testid="quote-download-btn" onClick={download} disabled={downloading}
              className="btn-terra inline-flex items-center gap-1.5 rounded-full px-5 py-2 text-xs font-semibold disabled:opacity-60">
              <Download className="w-3.5 h-3.5" /> {downloading ? "Készül..." : "PDF letöltése"}
            </button>
          </div>
        )}
      </header>
      <main className="py-10 px-4 overflow-x-auto print:p-0">
        {quote ? (
          <div className="mx-auto shadow-2xl print:shadow-none" style={{ width: 794 }}>
            <QuoteDocument ref={docRef} quote={quote} />
          </div>
        ) : (
          <p className="text-center text-[#8c8882]">Árajánlat betöltése...</p>
        )}
      </main>
    </div>
  );
}
